import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MessageCircle, Upload, RotateCcw, Save, Film } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CHAPTERS } from "@/lib/tutorial";
import { Button, Card, Field, Input, Page, PageHeader } from "@/components/kit";

const CHUNK = 4 * 1024 * 1024;
const MAX = 100 * 1024 * 1024;
const PROFILE = { all: "Todos", mgmt: "Gerentes e admin", admin: "Admin", oper: "Colaboradores" };

function SupportCard({ current }) {
  const qc = useQueryClient();
  const [num, setNum] = useState("");
  useEffect(() => { setNum(current || ""); }, [current]);
  const save = useMutation({
    mutationFn: () => api("/master/platform", { method: "PUT", body: { support_whatsapp: num } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["master-platform"] }); qc.invalidateQueries({ queryKey: ["platform"] }); toast.success("WhatsApp do suporte salvo"); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Card className="flex flex-col gap-3" data-testid="support-card">
      <div className="label flex items-center gap-2"><MessageCircle size={16} style={{ color: "#1F8A4C" }} /> WhatsApp do suporte</div>
      <p className="caption">Usado no botão “Ainda com dúvida?” de cada capítulo do tutorial, para todos os restaurantes. Deixe vazio para esconder o botão.</p>
      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[200px]"><Field label="Número com DDD"><Input value={num} onChange={(e) => setNum(e.target.value)} placeholder="(98) 98100-4999" inputMode="tel" data-testid="support-whatsapp-input" /></Field></div>
        <Button icon={Save} loading={save.isPending} onClick={() => save.mutate()} data-testid="support-whatsapp-save">Salvar</Button>
      </div>
    </Card>
  );
}

async function uploadChunks(chapter, file, onProgress) {
  const id = crypto.randomUUID();
  const total = Math.ceil(file.size / CHUNK);
  let last;
  for (let i = 0; i < total; i += 1) {
    const form = new FormData();
    form.append("upload_id", id); form.append("index", String(i)); form.append("total", String(total)); form.append("filename", file.name);
    form.append("file", file.slice(i * CHUNK, (i + 1) * CHUNK), file.name);
    last = await api(`/master/tutorial-video/${chapter}`, { method: "POST", form });
    onProgress(Math.round((100 * (i + 1)) / total));
  }
  return last;
}

function VideoRow({ ch, custom }) {
  const qc = useQueryClient();
  const ref = useRef();
  const [pct, setPct] = useState(null);
  const src = custom ? `${process.env.REACT_APP_BACKEND_URL}${custom}` : `/tutorial/v-${ch.id}.mp4`;
  const done = () => { qc.invalidateQueries({ queryKey: ["master-platform"] }); qc.invalidateQueries({ queryKey: ["platform"] }); };
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > MAX) { toast.error("Vídeo muito grande (máx. 100 MB)"); return; }
    setPct(0);
    try { await uploadChunks(ch.id, f, setPct); done(); toast.success(`Vídeo de “${ch.title}” atualizado`); } catch (err) { toast.error(err.message); } finally { setPct(null); }
  };
  const reset = useMutation({ mutationFn: () => api(`/master/tutorial-video/${ch.id}`, { method: "DELETE" }), onSuccess: () => { done(); toast.success("Vídeo padrão restaurado"); } });
  return (
    <div className="grid sm:grid-cols-[220px_1fr] gap-4 py-4" data-testid={`video-row-${ch.id}`}>
      <video key={src} src={src} poster={ch.cover} controls preload="none" playsInline className="w-full aspect-video rounded-xl" style={{ background: "#111" }} />
      <div className="flex flex-col gap-2">
        <div className="label">{ch.title}</div>
        <div className="caption">{PROFILE[ch.role]} · <span data-testid={`video-source-${ch.id}`}>{custom ? "vídeo enviado por você" : "gravação padrão"}</span></div>
        {pct !== null && <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--surface-alt)" }}><div className="h-full" style={{ width: `${pct}%`, background: "var(--brand)" }} data-testid={`video-progress-${ch.id}`} /></div>}
        <div className="flex flex-wrap gap-2 mt-1">
          <input ref={ref} type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={pick} data-testid={`video-file-input-${ch.id}`} />
          <Button size="sm" variant="secondary" icon={Upload} loading={pct !== null} onClick={() => ref.current?.click()} data-testid={`video-upload-${ch.id}`}>Trocar vídeo</Button>
          {custom && <Button size="sm" variant="ghost" icon={RotateCcw} onClick={() => reset.mutate()} data-testid={`video-reset-${ch.id}`}>Voltar ao padrão</Button>}
        </div>
      </div>
    </div>
  );
}

export default function TutorialSupport() {
  const { data } = useQuery({ queryKey: ["master-platform"], queryFn: () => api("/master/platform") });
  return (
    <Page>
      <PageHeader title="Tutorial e suporte" subtitle="Vídeos dos capítulos e contato de suporte exibidos para todos os restaurantes" />
      <div className="flex flex-col gap-6">
        <SupportCard current={data?.support_whatsapp} />
        <Card data-testid="videos-card">
          <div className="label flex items-center gap-2"><Film size={16} style={{ color: "var(--brand)" }} /> Vídeos dos capítulos</div>
          <p className="caption mt-1">MP4, WEBM ou MOV até 100 MB. O envio é feito em partes, então pode fechar só depois que a barra completar.</p>
          <div className="divide">{CHAPTERS.map((c) => <VideoRow key={c.id} ch={c} custom={data?.videos?.[c.id]} />)}</div>
        </Card>
      </div>
    </Page>
  );
}
